'use client';

/**
 * SectionRenderer - Dispatches to appropriate section component based on config
 *
 * Maps SectionConfig.type to the corresponding React component.
 * Handles conditional rendering and field extraction.
 */

import type { SectionConfig, DateField } from './sections/types';
import {
  ImageSection,
  StatusSection,
  DatesSection,
  AmountSection,
  PartySection,
  ItemsSection,
  ResponsesSection,
  StatsSection,
  ListSection,
  ContactSection,
  ProductsSection,
  CollectionsSection,
  TextSection,
  TagsSection,
  IntentLinesSection,
  AxisWeightsSection,
  MetadataSection,
  CcFunctionsSection,
  SectionSkeleton,
} from './sections';

interface SectionRendererProps {
  config: SectionConfig;
  data: Record<string, unknown>;
  entityType: string;
  entityId: string;
  isLoading?: boolean;
  // Click-to-focus on an `items` section's line rows — OFF by default.
  // Only the Cost Plan rail surface (mode3/mode11) turns this on; see
  // DetailsPanel's `interactive` prop docstring.
  interactive?: boolean;
  focusedSourceLineSid?: string | null;
  onItemClick?: (item: Record<string, unknown>) => void;
}

/**
 * Safely get a nested value from an object using dot notation.
 * e.g., getValue(data, "images.primary") returns data.images.primary
 */
function getValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce((acc: unknown, key) => {
    if (acc && typeof acc === 'object' && key in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}

/**
 * Simple condition evaluator.
 * Supports basic expressions like "data.vendor_responses?.length > 0"
 */
function evaluateCondition(condition: string, data: Record<string, unknown>): boolean {
  try {
    // Create a safe evaluation function
    const fn = new Function('data', `return ${condition}`);
    return Boolean(fn(data));
  } catch (err) {
    console.warn(`[SectionRenderer] Failed to evaluate condition: ${condition}`, err);
    return true; // Default to showing section on error
  }
}

export function SectionRenderer({
  config,
  data,
  entityType,
  entityId,
  isLoading = false,
  interactive = false,
  focusedSourceLineSid = null,
  onItemClick,
}: SectionRendererProps) {
  // Show skeleton while loading
  if (isLoading) {
    return <SectionSkeleton type={config.type} />;
  }

  // Conditional rendering - check condition against data
  if (config.condition && !evaluateCondition(config.condition, data)) {
    return null;
  }

  // Extract primary field value
  const value = config.field ? getValue(data, config.field) : null;

  // Extract fallback value (for images)
  const fallbackValue = config.fallback_field
    ? getValue(data, config.fallback_field)
    : null;

  // Extract gallery URLs (for image galleries)
  const galleryValue = config.gallery_field
    ? getValue(data, config.gallery_field)
    : null;

  // Extract multiple field values (for dates, stats, contact)
  const values = config.fields?.map((f) => {
    if (typeof f === 'string') {
      return { field: f, value: getValue(data, f) };
    }
    // DateField object
    const df = f as DateField;
    return { field: df.field, label: df.label, value: getValue(data, df.field) };
  });

  // Dispatch to appropriate section component
  switch (config.type) {
    case 'image':
      return (
        <ImageSection
          src={value as string | null}
          fallbackSrc={fallbackValue as string | null}
          alt={data.name as string}
          aspectRatio={config.aspect_ratio}
          galleryUrls={galleryValue as string[] | null}
        />
      );

    case 'status':
      return <StatusSection status={value as string | null} entityType={entityType} />;

    case 'dates':
      return (
        <DatesSection
          dates={(values || []).map((v) => ({
            label: v.label || v.field,
            value: v.value as string | null,
          }))}
        />
      );

    case 'amount': {
      const amt = (value as number | null) || (fallbackValue as number | null);
      return (
        <AmountSection
          amount={amt}
          label={config.label}
        />
      );
    }

    case 'party':
      return (
        <PartySection
          party={value as Record<string, unknown> | string | null}
          label={config.label}
          showContacts={config.show_contacts}
        />
      );

    case 'items':
      return (
        <ItemsSection
          items={value as Array<Record<string, unknown>> | null}
          showTotal={config.show_total}
          label={config.label}
          total={data.amount_total as number | null | undefined}
          interactive={interactive}
          focusedSourceLineSid={focusedSourceLineSid}
          onRowClick={onItemClick}
        />
      );

    case 'responses':
      return (
        <ResponsesSection
          responses={value as Array<Record<string, unknown>> | null}
          label={config.label}
        />
      );

    case 'stats':
      return (
        <StatsSection
          stats={(values || []).map((v) => ({
            field: v.field,
            value: v.value as number | string | null,
            label: v.label,
          }))}
          label={config.label}
        />
      );

    case 'list':
      return (
        <ListSection
          items={value as Array<Record<string, unknown>> | null}
          label={config.label}
          maxItems={config.max_items}
          entityType={entityType}
        />
      );

    case 'contact':
      return (
        <ContactSection
          data={data}
          fields={config.fields as string[] | null}
          label={config.label}
        />
      );

    case 'products':
      return (
        <ProductsSection
          products={value as Array<Record<string, unknown>> | null}
          label={config.label}
          maxItems={config.max_items}
        />
      );

    case 'collections':
      return (
        <CollectionsSection
          collections={value as Array<Record<string, unknown>> | null}
          label={config.label}
          maxItems={config.max_items}
        />
      );

    case 'text':
      return (
        <TextSection
          text={value as string | null}
          label={config.label}
        />
      );

    case 'tags':
      return (
        <TagsSection
          fields={(config.fields || []).filter((f): f is string => typeof f === 'string')}
          data={data}
          label={config.label}
        />
      );

    case 'intent_lines':
      return (
        <IntentLinesSection
          lines={value as Array<{
            line_sid?: string | null;
            name: string;
            qty?: number | null;
            target_price?: number | null;
            brief?: string | null;
            intent_description?: string | null;
          }> | null}
          label={config.label}
        />
      );

    case 'axis_weights':
      return (
        <AxisWeightsSection
          axes={value as Array<{
            name: string;
            weight: number;
            default: number;
            changed?: boolean;
          }> | null}
          rationale={
            typeof fallbackValue === 'string' ? fallbackValue : null
          }
          label={config.label}
        />
      );

    case 'cc_functions':
      return (
        <CcFunctionsSection
          functions={value as Array<Record<string, unknown>>}
          label={config.label}
        />
      );

    case 'metadata':
      return <MetadataSection semanticId={entityId} />;

    default:
      console.warn(`[SectionRenderer] Unknown section type: ${config.type}`);
      return null;
  }
}
